import { useMobileBoxLayout } from './full/BoxLayouts';
import { FullBoxFrame } from './full/FullBoxFrame';
import { BoxGameShell } from './full/BoxGameShell';
import { PageHeading } from '@src/components/PageHeading/PageHeading';
import './ParticipantSelect.css';
import { CardRecover } from './CardRecover';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { type BoxInfo, type BoxRecoverCard } from '@lub0v/santa-utils';
import {
  Button,
  Container,
  Card,
  Panel,
  showDialog,
  closeDialog,
  Spinner,
  Stack,
  Typography,
} from '@src/components';

import { currentBoxSubdomain } from '@src/routing/boxDomain';
import { santaClient } from '@src/state/client';

export interface BoxRecoverProps {
  boxKey: string;
}

/** A bare game link shows a public summary and anonymous organizer recovery only. */
export function BoxRecover({ boxKey }: BoxRecoverProps) {
  const [organizerKey, setOrganizerKey] = useState<string | null>(null);
  const mobile = useMobileBoxLayout();
  const participantsRef = useRef<HTMLElement | null>(null);
  const [gameInfo, setGameInfo] = useState<BoxInfo | null>(null);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState<BoxRecoverCard[]>([]);
  useEffect(() => {
    let cancelled = false;
    void santaClient
      .getBoxRecoverCards({ key: boxKey })
      .then((result) => {
        if (cancelled) {
          return;
        }
        const preview = result.ok ? result.data : undefined;
        setGameInfo(preview?.gameInfo ?? null);
        setCards(preview?.cards ?? []);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [boxKey]);

  const organizerCards = cards.filter((card) => card.isAdmin);
  function recoverOrganizer() {
    if (organizerCards.length === 1) {
      setOrganizerKey(organizerCards[0].randomKey);
      return;
    }
    showDialog({
      title: t('box.recover.iAmOrganizer'),
      children: (
        <OrganizerRecoveryChoices
          boxKey={boxKey}
          cards={organizerCards}
          onPick={(key) => {
            closeDialog();
            setOrganizerKey(key);
          }}
        />
      ),
      cancel: t('box.close'),
      testId: 'box-organizer-recovery-choices',
    });
  }

  if (loading) {
    return (
      <Stack gap={4} align="center" testId="box-recover-loading">
        <Spinner size="medium" label={t('box.loading')} />
      </Stack>
    );
  }

  const content = (
    <Container
      width="measure"
      grow
      className={`box-select-page${gameInfo?.box.mode === 'full' ? ' box-select-page--ground' : ''}`}
    >
      <Stack gap={8} wide className="box-select-page__stack" testId="box-recover">
        <Panel edges={{ pieces: 3 }} padding="large" className="box-select-page__private-panel" testId="box-private-access-panel">
          <Stack gap={5} wide>
            <PageHeading
              title={t('box.recover.privateTitle')}
              subtitle={t('box.recover.privateBody')}
            />
            <Card
              tone="info"
              surface="tint"
              icon
              className="box-select-page__invite-hint"
              testId="box-invite-hint"
            >
              <Stack gap={2} wide>
                <Typography as="p" role="body/sm" color="inherit">
                  {t('box.recover.privateLinkHint')}
                </Typography>
                <Typography
                  as="code"
                  role="meta/mono"
                  className="box-select-page__invite-example"
                  testId="box-invite-example"
                >
                  {`${window.location.origin}${currentBoxSubdomain() ? '/' : `/box/${boxKey}`}?join=<${t('box.recover.inviteCode')}>`}
                </Typography>
              </Stack>
            </Card>
            <Stack gap={6} align="center" wide className="box-select-page__footer">
              {organizerCards.length > 0 && (
                <Button
                  variant="secondary"
                  onClick={recoverOrganizer}
                  testId="box-recover-organizer"
                >
                  {t('box.recover.iAmOrganizer')}
                </Button>
              )}
            </Stack>
          </Stack>
        </Panel>
        {organizerKey && (
          <CardRecover key={organizerKey} boxKey={boxKey} cardKey={organizerKey} embedded />
        )}
      </Stack>
    </Container>
  );
  if (!gameInfo)
    return (
      <BoxGameShell info={null} cards={[]} main={false}>
        {content}
      </BoxGameShell>
    );
  const frame = (
    <FullBoxFrame
      info={gameInfo}
      cards={[]}
      route={{ key: boxKey, view: 'home', card: false }}
      onChanged={() => {}}
      participantsRef={participantsRef}
    >
      {content}
    </FullBoxFrame>
  );
  return mobile ? (
    frame
  ) : (
    <BoxGameShell info={gameInfo} cards={[]} main>
      {frame}
    </BoxGameShell>
  );
}

function OrganizerRecoveryChoices({
  boxKey,
  cards,
  onPick,
}: {
  boxKey: string;
  cards: BoxRecoverCard[];
  onPick: (key: string) => void;
}) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<{ key: string; email: string }[] | null>(null);
  useEffect(() => {
    let active = true;
    void Promise.all(
      cards.map(async (card) => {
        const result = await santaClient.getCardAccessRecoverMethods({
          key: boxKey,
          cardKey: card.randomKey,
          token: '',
        });
        return { key: card.randomKey, email: result.data?.emailMasked || '' };
      }),
    ).then((result) => {
      if (active) setOptions(result);
    });
    return () => {
      active = false;
    };
  }, [boxKey, cards]);
  return (
    <Stack gap={4}>
      {options ? (
        options.map((option, index) => (
          <Button key={option.key} variant="secondary" onClick={() => onPick(option.key)}>
            {option.email || t('box.recover.organizerOption', { number: index + 1 })}
          </Button>
        ))
      ) : (
        <Spinner label={t('box.loading')} />
      )}
    </Stack>
  );
}
